import React, { useState, useEffect } from "react";
import config from "../Common/Configurations/APIConfig";
import apiService from "../Common/Configurations/APIService";
import SupplierHeader from "../Supplier/SupplierHeader";
import SupplierTabs from "./SupplierTabs";
import { useAuth } from "../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import DeleteAlert from "../Common/Layout/DeleteAlert";
import jsonData from "../VisaData/b2bvisas.json";

const initialFieldValues = {
  supplierVisaPriceId: "00000000-0000-0000-0000-000000000000",
  supplierId: "D05F9DAF-77F1-4BA2-8252-1DD92334670D",
  visaId: "",
  adultPrice: 0,
  childPrice: 0,
};

const VisaPrice = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [loadingStates, setLoadingStates] = useState({});
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredVisas, setFilteredVisas] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryVisas, setCountryVisas] = useState([]);
  const [bulkPrices, setBulkPrices] = useState({});
  const supplierId = initialFieldValues.supplierId;

  // Function to get visa details by visaId from JSON data
  const getVisaDetails = (visaId) => {
    for (const country of jsonData) {
      const visa = country.visas.find((v) => v.visaId === visaId);
      if (visa) {
        return {
          ...visa,
          countryName: country.name,
          flagSrc: country.flagSrc,
        };
      }
    }
    return null;
  };

  // Function to find existing price record for a visa
  const findExistingPriceRecord = (visaId) => {
    return lists.find((item) => item.visaId === visaId);
  };

  const handleOpenDeleteModal = (visaId) => {
    setItemToDelete(visaId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleCountrySearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      const filtered = jsonData.filter((country) =>
        country.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredVisas(filtered);
      setIsDropdownVisible(true);
    } else {
      setFilteredVisas([]);
      setIsDropdownVisible(false);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setCountryVisas(country.visas);
    setFilteredVisas([]);
    setIsDropdownVisible(false);
    setSearchQuery(country.name);

    // Initialize bulk prices with existing data - don't show 0 for new records
    const initialBulkPrices = {};
    country.visas.forEach((visa) => {
      const existingRecord = findExistingPriceRecord(visa.visaId);
      initialBulkPrices[visa.visaId] = {
        supplierVisaPriceId: existingRecord
          ? existingRecord.supplierVisaPriceId
          : "00000000-0000-0000-0000-000000000000",
        adultPrice: existingRecord ? existingRecord.adultPrice : "",
        childPrice: existingRecord ? existingRecord.childPrice : "",
        isExisting: !!existingRecord,
      };
    });
    setBulkPrices(initialBulkPrices);
  };

  const handleBulkPriceChange = (visaId, field, value) => {
    setBulkPrices((prev) => ({
      ...prev,
      [visaId]: {
        ...prev[visaId],
        [field]: value === "" ? "" : parseFloat(value) || 0,
      },
    }));
  };

  const handleReset = () => {
    setSelectedCountry(null);
    setCountryVisas([]);
    setSearchQuery("");
    setBulkPrices({});
    setFilteredVisas([]);
    setIsDropdownVisible(false);
    setLoadingStates({});
  };

  const GetData = async (pageNumber) => {
    const res = await apiService.get(
      `${config.GETSUPPLIERVISAPRICESBYSUPPLIERID}/${supplierId}`,
      pageSize,
      pageNumber
    );

    // Enhance the data with visa details from JSON
    const enhancedData = res.data.map((item) => ({
      ...item,
      visaDetails: getVisaDetails(item.visaId),
    }));

    setLists(enhancedData);
    setPageNumber(res.pageNumber);
    setPageSize(res.pageSize);
    setTotalPages(res.totalPages);
    setData(res.data);
    setTotalRecords(res.totalRecords);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        // Find the record to delete
        const recordToDelete = lists.find(
          (item) => item.visaId === itemToDelete
        );
        if (recordToDelete) {
          await apiService.remove(
            config.DELETESUPPLIERVISAPRICE,
            recordToDelete.supplierVisaPriceId
          );
        }

        // Reset the values to empty in the form instead of 0
        if (selectedCountry && bulkPrices[itemToDelete]) {
          setBulkPrices((prev) => ({
            ...prev,
            [itemToDelete]: {
              ...prev[itemToDelete],
              adultPrice: "",
              childPrice: "",
              isExisting: false,
              supplierVisaPriceId: "00000000-0000-0000-0000-000000000000",
            },
          }));
        }

        await GetData(pageNumber);
      } catch (error) {
        console.error("Error deleting price:", error);
      }
      handleCloseDeleteModal();
    }
  };

  const validateBulkPrices = () => {
    for (const visaId in bulkPrices) {
      const price = bulkPrices[visaId];
      if (
        !price.adultPrice ||
        !price.childPrice ||
        price.adultPrice <= 0 ||
        price.childPrice <= 0
      ) {
        return false;
      }
    }
    return Object.keys(bulkPrices).length > 0;
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!validateBulkPrices()) {
      alert("Please enter valid prices for all visas");
      return;
    }

    setLoadingStates((prev) => ({ ...prev, bulk: true }));
    try {
      const promises = Object.keys(bulkPrices).map((visaId) => {
        const priceData = bulkPrices[visaId];
        const formData = {
          supplierVisaPriceId: priceData.supplierVisaPriceId,
          supplierId: supplierId,
          visaId: visaId,
          adultPrice: priceData.adultPrice,
          childPrice: priceData.childPrice,
        };

        // Use UPDATE for existing records, CREATE for new ones
        if (priceData.isExisting) {
          return apiService.update(config.UPDATESUPPLIERVISAPRICE, formData);
        } else {
          return apiService.create(config.CREATEUPPLIERVISAPRICE, formData);
        }
      });

      await Promise.all(promises);
      await GetData(pageNumber);
      handleReset();
    } catch (error) {
      console.error("Error submitting bulk prices:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, bulk: false }));
    }
  };

  const handleSingleUpdate = async (visaId, priceData) => {
    if (
      !priceData.adultPrice ||
      !priceData.childPrice ||
      priceData.adultPrice <= 0 ||
      priceData.childPrice <= 0
    ) {
      alert("Please enter valid prices");
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [visaId]: true }));
    try {
      const formData = {
        supplierVisaPriceId: priceData.supplierVisaPriceId,
        supplierId: supplierId,
        visaId: visaId,
        adultPrice: priceData.adultPrice,
        childPrice: priceData.childPrice,
      };

      // Use UPDATE for existing records, CREATE for new ones
      if (priceData.isExisting) {
        await apiService.update(config.UPDATESUPPLIERVISAPRICE, formData);
      } else {
        await apiService.create(config.CREATEUPPLIERVISAPRICE, formData);
      }

      await GetData(pageNumber);

      // Refresh the bulk prices data to reflect the update
      if (selectedCountry) {
        const updatedBulkPrices = { ...bulkPrices };
        updatedBulkPrices[visaId] = {
          ...priceData,
          isExisting: true, // Mark as existing after successful creation/update
        };
        setBulkPrices(updatedBulkPrices);
      }
    } catch (error) {
      console.error("Error updating price:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [visaId]: false }));
    }
  };

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <SupplierHeader User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <SupplierTabs pageName="PRICES" />
                </div>

                {/* Search and Bulk Update Section */}
                <div className="card-body">
                  <div className="row g-3 align-items-end">
                    <div className="col-lg-6">
                      <label htmlFor="countrySearch" className="form-label">
                        Search Country
                      </label>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleCountrySearch}
                        className="form-control"
                        placeholder="Search for country (e.g., India)"
                      />
                      {isDropdownVisible && filteredVisas.length > 0 && (
                        <div className="position-relative">
                          <div
                            className="list-group mt-1 position-absolute w-100"
                            style={{
                              zIndex: 1000,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            {filteredVisas.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                className="list-group-item list-group-item-action"
                                onClick={() => handleCountrySelect(country)}
                              >
                                <div className="d-flex align-items-center">
                                  <img
                                    src={`https://flagcdn.com/w20/${country.flagSrc.toLowerCase()}.png`}
                                    alt={country.name}
                                    className="me-2"
                                    style={{ width: "20px", height: "15px" }}
                                  />
                                  <span>{country.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-lg-1">
                      <div className="hstack gap-2">
                        <button
                          type="button"
                          className="btn btn-danger w-100"
                          onClick={handleReset}
                        >
                          <i className="ri-refresh-line align-bottom me-1" />{" "}
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Price Update Form */}
                  {selectedCountry && countryVisas.length > 0 && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          Update Prices for {selectedCountry.name} Visas
                          <span className="badge bg-primary ms-2">
                            {countryVisas.length} visa types
                          </span>
                        </h5>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleBulkSubmit}
                          disabled={loadingStates.bulk}
                        >
                          {loadingStates.bulk
                            ? "Please wait..."
                            : bulkPrices[countryVisas[0]?.visaId]?.isExisting
                            ? "Update All Prices"
                            : "Add All Prices"}
                        </button>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead className="bg-light">
                            <tr>
                              <th>Visa Type</th>
                              <th>Adult Price</th>
                              <th>Child Price</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {countryVisas.map((visa) => (
                              <tr key={visa.visaId}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={`https://flagcdn.com/w20/${selectedCountry.flagSrc.toLowerCase()}.png`}
                                      alt={selectedCountry.name}
                                      className="me-2"
                                      style={{ width: "20px", height: "15px" }}
                                    />
                                    <strong>{visa.visaName}</strong>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={
                                      bulkPrices[visa.visaId]?.adultPrice || ""
                                    }
                                    onChange={(e) =>
                                      handleBulkPriceChange(
                                        visa.visaId,
                                        "adultPrice",
                                        e.target.value
                                      )
                                    }
                                    className="form-control"
                                    placeholder="Enter adult price"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={
                                      bulkPrices[visa.visaId]?.childPrice || ""
                                    }
                                    onChange={(e) =>
                                      handleBulkPriceChange(
                                        visa.visaId,
                                        "childPrice",
                                        e.target.value
                                      )
                                    }
                                    className="form-control"
                                    placeholder="Enter child price"
                                  />
                                </td>
                                <td>
                                  <span
                                    className={`badge ${
                                      bulkPrices[visa.visaId]?.isExisting
                                        ? "bg-success"
                                        : "bg-warning"
                                    }`}
                                  >
                                    {bulkPrices[visa.visaId]?.isExisting
                                      ? "Existing"
                                      : "New"}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      type="button"
                                      className={`btn btn-sm ${
                                        bulkPrices[visa.visaId]?.isExisting
                                          ? "btn-primary"
                                          : "btn-success"
                                      }`}
                                      onClick={() =>
                                        handleSingleUpdate(
                                          visa.visaId,
                                          bulkPrices[visa.visaId]
                                        )
                                      }
                                      disabled={loadingStates[visa.visaId]}
                                    >
                                      {loadingStates[visa.visaId]
                                        ? "..."
                                        : bulkPrices[visa.visaId]?.isExisting
                                        ? "Update"
                                        : "Add"}
                                    </button>

                                    {bulkPrices[visa.visaId]?.isExisting && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() =>
                                          handleOpenDeleteModal(visa.visaId)
                                        }
                                        title="Delete"
                                      >
                                        <i className="ri-delete-bin-line" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default VisaPrice;
